import type { IPdfCustomReportTemplateListItem } from '@/Interfaces/PdfBuilder/Template.interface';
import { deletePdfTemplate, fetchPdfTemplates, sharePdfTemplate } from '@/store/api/pdfBuilderTemplate.api';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

export function usePdfTemplateReports() {
  const [templates, setTemplates] = useState<IPdfCustomReportTemplateListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [shareTargetId, setShareTargetId] = useState<number | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPdfTemplates();
      setTemplates(data);
    } catch {
      toast.error('Failed to load report templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const onShareTemplate = useCallback((template: IPdfCustomReportTemplateListItem) => {
    setShareTargetId(template.id);
  }, []);

  const onShareModalClose = useCallback(() => {
    setShareTargetId(null);
  }, []);

  const onSubmitShare = useCallback(
    async ({
      receivingUserIds,
      shareWithOrg,
      targetId,
    }: { receivingUserIds: number[]; shareWithOrg: boolean; targetId: number }) => {
      try {
        await sharePdfTemplate(targetId, { receivingUserIds, shareWithOrg });
        toast.success('Template successfully shared.');
      } catch {
        toast.error('Something went wrong on sharing attempt.');
      } finally {
        setShareTargetId(null);
      }
    },
    [],
  );

  const onDeleteTemplate = useCallback(
    async (template: IPdfCustomReportTemplateListItem) => {
      try {
        await deletePdfTemplate(template.id);
        toast.success('Template deleted.');
        await loadTemplates();
      } catch {
        toast.error('Failed to delete template.');
      }
    },
    [loadTemplates],
  );

  return {
    templates,
    loading,
    shareTargetId,
    onShareTemplate,
    onShareModalClose,
    onSubmitShare,
    onDeleteTemplate,
    reloadTemplates: loadTemplates,
  };
}
